/* ════════════════════════════════════════════════════════════════════════════
   ManDana — the properties band (CS-7)
   ---------------------------------------------------------------------------
   A record made inside a project inherits four facts the instant it exists: the
   project, that project's part of life, the status its workflow starts
   everything at, and the day it lands on. The compose sheet said none of them.
   The only trace was a coloured dot in the toolbar — a category decided without
   ever being named — and a When button that gives a day without saying which
   project the day belongs to.

   This is the band that says them. It is drawn only for a PARENTED creation,
   which is the honest limit: a thought captured from the day's "+" has no
   parents to show, and a strip of rows that all read "—" would be worse than
   the nothing that is there now.

   ── IT IS THE INFO PANEL, COLLAPSED (B1) ─────────────────────────────────
   Two places showing the same facts is the duplication this whole phase exists
   to end, so the band is not a second surface that will later sit beside a
   panel — it is the panel's closed state. Closed it shows the three or four
   that matter; CS-11 opens it, expands those same rows, and joins the rest to
   them. That is also why this file exists at all rather than a component
   inside md-compose.jsx: the panel is coming here, and it needs the Workshop's
   property vocabulary.

   ── WHY IT IS ITS OWN FILE, LOADED LATE ──────────────────────────────────
   The rows wear md-projects.jsx's own property parts — `WsProp`, `WsPick`,
   `StatusMark` — because a project's page, a backlog record's page and a
   record in the sheet are the same six facts at three altitudes, and three
   copies drift one small fix at a time. md-compose.jsx loads BEFORE
   md-projects.jsx (index.html), so it cannot hold them. This file loads after
   both and publishes one component the sheet mounts through `window`.

   ── ONE WRITER, AND IT IS NOT THIS FILE ──────────────────────────────────
   md-project.ts decides what a status move writes — the word, the tick, or
   both. This file asks `onRecordStatus` when the app gave it one (md-app's
   door, which re-reads the day state it holds) and md-project.ts's own door
   when it did not. It never patches a map itself.
   ════════════════════════════════════════════════════════════════════════════ */

/* eslint-disable no-undef */
(function () {
  const { useState: useStPR, useRef: useRefPR } = React;

  const inkPR = (a) => window.ink(a);
  const isLightPR = () => window.mdIsLight();

  /* ── the status of a record, in its own workflow's words ──────────────────
     ProjectStatus (md-projects.jsx:388) is this control for a PROJECT, and the
     two cannot be one component: a project's status is a field on the project
     document, a record's is a word in `md:recordStatus` with a tick in a day
     beside it, and the doors, the setters and the fallbacks all differ. What
     IS shared is everything a person sees — the six silhouettes, the borrowed
     menu, the one escape stack — so the two read as the same control saying
     something about two different things.

     THE MARK IS DRAWN FROM THE RESOLVED STATUS, never from the stored word. A
     word this workflow has never heard of reads as its Inbox and waits there
     (md-project.ts:1196), which is what lets somebody swap a vocabulary and
     swap it back without losing where anything stood. */
  function BandStatus({ record, scheduled, accent, onWrite, pending = null }) {
    const light = isLightPR();
    const MdWs = window.MdWs || {};
    const { StatusMark } = MdWs;
    const [open, setOpen] = useStPR(false);
    const [, bump] = useStPR(0);
    const btn = useRefPR(null);
    const flow = window.mdWorkflowForRecord ? window.mdWorkflowForRecord(record) : null;
    const stored = window.mdRecordStatusOf ? window.mdRecordStatusOf(record, scheduled) : null;
    const statuses = (flow && flow.statuses) || [];
    /* `pending` is the seed a board column handed down for a record that does
       not exist yet (CS-10). It stands in for the stored answer only while
       there is nothing stored to read — a real row always speaks for itself. */
    const seeded = pending !== null
      ? statuses.find((row) => row.id === pending) || null : null;
    const here = seeded || stored;
    const pick = (id) => {
      setOpen(false);
      onWrite(id);
      bump((n) => n + 1);         // the engine's answer is re-read on the next draw
    };
    return <span className={light ? 'md-db-light' : ''}
      style={{ position: 'relative', display: 'inline-flex', '--db-accent': accent }}>
      <button type="button" ref={btn} onClick={() => setOpen(!open)}
        aria-label={`Status — ${here ? here.name : 'Inbox'}`} aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 26,
          padding: '4px 9px 4px 7px', border: `1px solid ${inkPR(light ? 0.1 : 0.12)}`,
          borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
          color: inkPR(0.82), font: 'inherit', fontSize: 12.5, cursor: 'pointer',
          transition: 'background .14s, border-color .14s' }}>
        {StatusMark ? <StatusMark category={here ? here.category : null}
          color={(here && here.color) || null} /> : null}
        <span style={{ whiteSpace: 'nowrap' }}>{here ? here.name : 'Inbox'}</span>
      </button>
      {open && window.DbMenu &&
      <window.DbMenu title="Status" onClose={() => setOpen(false)} anchor={btn}
        style={{ position: 'absolute', top: 30, left: 'auto', right: 'auto', insetInlineStart: -7,
          maxHeight: 320, overflowY: 'auto', zIndex: 40 }}>
        <div role="listbox" aria-label="Status">
          {statuses.map((row) => {
            const on = !!here && here.id === row.id;
            return <button key={row.id} type="button" role="option"
              aria-selected={on ? 'true' : 'false'} className="md-db-menu-row"
              onClick={() => pick(row.id)}>
              {StatusMark ? <StatusMark category={row.category} color={row.color || null} /> : null}
              <span>{row.name}</span>
            </button>;
          })}
        </div>
      </window.DbMenu>}
    </span>;
  }

  /* ── THE BAND STOOD HERE, AND THE PILLS ARE WHAT IT WAS FOR (P4) ─────────
     `MdPropsBand` drew a record's four inherited facts above the writing, for a
     PARENTED creation only — the honest limit while it was the only surface
     saying them, because a strip of rows all reading "—" over a thought
     captured from the day's "+" is worse than nothing.

     The compose sheet wears the beginning's own pills now, on every sheet, and
     they answer the same question with the same controls. Keeping the band
     beside them would have been two COMPONENTS of one fact — not two views of
     one fact, which is what a properties panel legitimately is, but two pieces
     of code to fix separately when the answer changes.

     `BandStatus` below outlived it: the panel's Status row is the same control,
     and it kept the name it was born with. */

  /* ══════════════════════════════════════════════════════════════════════════
     THE INFO PANEL (CS-11)
     ---------------------------------------------------------------------------
     The band above is this, closed. Opened, the same four properties expand and
     the rest of what a record is joins them: what links here, what it points
     at, what it is tagged. Before this there were three doors to those facts —
     the band, a "Linked records" popover behind More, and a Tag inspector one
     level deeper behind the same menu — which is precisely the duplication this
     phase exists to end. Both popovers are deleted, not bypassed.

     ── THE SECTIONS ARE DATA, AND THAT IS THE STORY'S REAL DEBT (B2) ─────────
     Activity — your notes and the record's own history in one chronological
     column — is deferred entirely this phase. What CS-11 owes it is not a stub
     but a SHAPE: `INFO_SECTIONS` is a list, every section is a heading and a
     body, so the fifth is one more entry rather than a re-cut layout. A column
     of four hand-built divs would satisfy every other line of this file and
     fail the only one the next phase depends on. Nothing here is named for the
     smaller half of that idea, deliberately — when it lands it is one log
     carrying both your notes and the record's own changes, and a name chosen
     now would commit the shape to only the first of them.

     ── IT DOES NOT WRITE ─────────────────────────────────────────────────────
     Every row here is a door to a writer that already exists — the band's own
     `writeStatus`, the sheet's project and domain pickers, the editor's tag
     API. A tag is `#Fajr` written in the body, so removing one is an edit to
     the text; the panel asks the editor and never reaches for the string.
     ══════════════════════════════════════════════════════════════════════════ */

  /* The label column is `.md-db-row-properties`' own, restated as inline style
     for one reason: that CSS also carries the dark database surface's colours
     and its bottom rule, and the panel is neither. The measurement is shared;
     the dress is not. The 700 breakpoint is DbRowPage's, so a phone gets the
     same 108 there and here. */
  function infoRowCols() {
    const narrow = typeof window.innerWidth === 'number' && window.innerWidth <= 700;
    return narrow ? '108px minmax(0,1fr)' : '145px minmax(0,1fr)';
  }

  function InfoRow({ label, children }) {
    return <div data-md-info-row="" style={{ display: 'grid',
      gridTemplateColumns: infoRowCols(), alignItems: 'center', minHeight: 37 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
        color: inkPR(0.4), fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis' }}>{label}</span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>;
  }

  /* A line of prose where a list would otherwise be an empty box (rule 6 —
     every state has a face). It says what to do, not that there is nothing. */
  function InfoNone({ children }) {
    return <div style={{ padding: '7px 2px 9px', fontSize: 12.5, lineHeight: 1.5,
      color: inkPR(0.42) }}>{children}</div>;
  }

  /* One row shape for every link in both link sections, because an outgoing
     link, a backlink and a link that resolves to nothing are the same sentence
     with different confidence. An unresolved one is DISABLED rather than
     hidden: you wrote it, so it is still a fact about this record — but it is
     not offered as a door, because pressing it could not succeed (rule 1). */
  function InfoLink({ row, accent, target, onOpen }) {
    const dead = !target;
    return <button type="button" disabled={dead} data-md-info-link=""
      onClick={() => {if (target && onOpen) onOpen(target);}}
      style={{ width: '100%', minHeight: 40, display: 'flex', alignItems: 'center', gap: 9,
        padding: '5px 8px', marginInlineStart: -8, border: 'none', borderRadius: 10,
        background: 'none', textAlign: 'start', cursor: dead ? 'default' : 'pointer',
        font: 'inherit', transition: 'background .14s ease' }}
      onPointerEnter={(e) => {if (!dead) e.currentTarget.style.background = inkPR(0.05);}}
      onPointerLeave={(e) => {e.currentTarget.style.background = 'none';}}>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
        background: dead ? 'transparent' : window.mdRgba(accent, 0.7),
        border: dead ? `1px dashed ${inkPR(0.42)}` : 'none' }} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis', fontSize: 13, fontWeight: 620,
          color: inkPR(dead ? 0.5 : 0.84) }}>{row.title || row.label}</span>
        {row.subtitle && <span style={{ display: 'block', marginTop: 1, fontSize: 11,
          color: inkPR(0.4) }}>{row.subtitle}</span>}
      </span>
    </button>;
  }

  /* ── the list ─────────────────────────────────────────────────────────────
     `body` takes the one context object the panel assembles, so a new section
     needs no new prop threading and no change here beyond its own entry. */
  const INFO_SECTIONS = [
    {
      id: 'properties',
      icon: 'fields',
      title: 'Properties',
      body: (c) => {
        const MdWs = window.MdWs || {};
        const { WsPick } = MdWs;
        const project = c.record.pid && window.mdProjectById
          ? window.mdProjectById(c.record.pid) : null;
        const domain = c.record.domain && window.mdDomainById
          ? window.mdDomainById(c.record.domain) : null;
        const when = [c.dayLabel, c.schedText].filter(Boolean).join(' · ');
        return <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InfoRow label="Project">
            {WsPick ? <WsPick label="Project" accent={c.accent} value={c.record.pid || null}
              none="No project" options={[{ id: null, name: 'No project' },
                ...c.projects.map((row) => ({ id: row.id, name: row.name }))]}
              onPick={(next) => c.onPickProject && c.onPickProject(next)} /> : null}
          </InfoRow>
          <InfoRow label="Part of life">
            {/* Stated, not offered, while a project owns it — the same refusal
                the band makes, for the same reason: md-project.ts would
                overrule a pick here on the very next save. */}
            {project
              ? <span title={`${domain ? domain.label : '—'}, from ${project.name}`}
                style={{ display: 'block', minWidth: 0, color: inkPR(0.84), fontSize: 13,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {domain ? domain.label : '—'}
                <span style={{ color: inkPR(0.36), marginInlineStart: 5 }}>· from {project.name}</span>
              </span>
              : (WsPick ? <WsPick label="Part of life" accent={c.accent}
                value={c.record.domain || null} none="Auto"
                options={[{ id: null, name: 'Auto' },
                  ...(window.MD_DOMAINS || []).map((row) => ({ id: row.id, name: row.label }))]}
                onPick={(next) => c.onPickDomain && c.onPickDomain(next)} /> : null)}
          </InfoRow>
          <InfoRow label="Status">
            <BandStatus record={c.record} scheduled={c.scheduled} accent={c.accent}
              onWrite={c.writeStatus} pending={c.pendingStatus} />
          </InfoRow>
          <InfoRow label={c.whenLabel}>
            <button type="button" onClick={c.onWhen} aria-label={`${c.whenLabel} — ${when || '—'}`}
              style={{ display: 'block', width: '100%', minHeight: 30, padding: '3px 7px',
                marginInlineStart: -7, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
                background: 'transparent', color: inkPR(0.84), font: 'inherit', fontSize: 13,
                textAlign: 'start', cursor: 'pointer', fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {when || '—'}
            </button>
          </InfoRow>
        </div>;
      },
    },
    {
      id: 'backlinks',
      icon: 'backlink',
      title: 'Backlinks',
      count: (c) => c.links.backlinks.length,
      body: (c) => (c.links.backlinks.length
        ? c.links.backlinks.map((row, i) => <InfoLink key={`b-${i}`} row={row} accent={c.accent}
          target={row.ref} onOpen={c.onOpenLinked} />)
        : <InfoNone>Nothing links to this record yet.</InfoNone>),
    },
    {
      id: 'related',
      icon: 'related',
      title: 'Related',
      count: (c) => c.links.outgoing.length + c.links.unresolved.length,
      body: (c) => ((c.links.outgoing.length || c.links.unresolved.length)
        ? <React.Fragment>
          {c.links.outgoing.map((row, i) => <InfoLink key={`o-${i}`} row={row} accent={c.accent}
            target={row.target} onOpen={c.onOpenLinked} />)}
          {c.links.unresolved.map((row, i) => <InfoLink key={`u-${i}`} row={row} accent={c.accent}
            target={null} onOpen={null} />)}
        </React.Fragment>
        : <InfoNone>Type [[ to link this record to another.</InfoNone>),
    },
    {
      id: 'tags',
      icon: 'tag',
      title: 'Tags',
      count: (c) => c.tags.active.length,
      /* The Tag inspector itself, unframed. md-compose.jsx owns it and loads
         first, so it is borrowed through `window` rather than copied — the
         search, the create row and "other records with this tag" are the same
         component whether it is a popover or a section. */
      body: (c) => (window.MdTagInspector
        ? <window.MdTagInspector framed={false} light={isLightPR()} accent={c.accent}
          active={c.tags.active} options={c.tags.options} sourceRef={c.tags.sourceRef}
          onAdd={c.onTagAdd} onRemove={c.onTagRemove} onOpen={c.onOpenLinked} />
        : <InfoNone>Type # in the record to tag it.</InfoNone>),
    },
  ];

  /* ── THE PANEL'S CHROME, LENT OUT ─────────────────────────────────────────
     A record's Details and a project draft's are the same surface: the same
     aside, the same sticky head, the same close, the same hairline when it is
     docked and the same row rhythm inside. Only the ROWS differ, because only
     the subjects do — one describes a thing that exists, the other a thing
     about to. So the chrome is a component both tenants stand in rather than a
     shape each of them redraws, for the same reason `MdSheetFrame` exists one
     storey up: two implementations of one surface can only drift, and these
     two already would have. */
  /* ── IT HAD A HEAD, AND THE HEAD SAID NOTHING (Marwan, 2026-08-21) ────────
     A sticky bar reading "Details", with a × beside it, under a button labelled
     Details that is lit while the panel is open. Three controls and one word,
     spent on a surface 340pt wide where every one of those pixels is a
     property. The word is on the button that opened it; the way back is that
     same button, Escape, or the tap-away — and the section bars below are the
     real headings, because they say what they are ABOUT rather than what
     surface you are on.

     A panel this narrow cannot afford a title bar that only repeats the door. */
  function MdInfoShell({ placement = 'overlay', children }) {
    const light = isLightPR();
    const docked = placement === 'dock';
    return <aside data-md-info-panel="" aria-label="Details" className="md-scroll"
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0,
        /* Fixed while docked, so the shell that animates it open can clip a
           panel whose own layout never changes (md-compose.jsx). */
        width: docked ? window.MD_INFO_PANEL_W || 340 : '100%',
        flex: docked ? 'none' : '1 1 auto', overflowY: 'auto',
        padding: '10px 18px 26px',
        /* Docked it is a column of the sheet and shares its ground, so it is
           divided by a hairline and nothing else — a second card inside the
           card would say these are two surfaces when they are one. */
        borderInlineStart: docked ? `0.75px solid ${inkPR(light ? 0.08 : 0.12)}` : 'none' }}>
      {children}
    </aside>;
  }

  /* ── a section is a bar, and a bar folds ──────────────────────────────────
     The heading used to be four words of grey uppercase floating over its own
     rows, which on a column of four sections gives no edge to read the
     structure by and nothing to press. It is a rounded bar now, with the count
     it already carried and a chevron at the trailing end, and pressing it folds
     the section away — the disclosure pattern the whole app already speaks.

     `<h3>` OUTSIDE, `<button>` INSIDE. A heading inside a button is not
     phrasing content and a screen reader loses the outline; wrapped this way it
     is both a level-3 heading and a disclosure, which is what it actually is.

     Folded is per-mount and starts open, deliberately. Where a panel is
     dismissed with one press and reopened with another, a remembered fold is a
     surface that greets you closed and makes you wonder what it is hiding.

     The bar itself moved to md-theme as `MdSectionBar` (2026-08-24): Tabs had
     grown a second one, and a component with two authors drifts. What stays
     here is what is local — the heading it sits inside, and the body it
     folds. */
  function MdInfoSection({ id, icon, title, count = 0, first = false, children }) {
    const [open, setOpen] = useStPR(true);
    const Bar = window.MdSectionBar;
    return <section data-md-info-section={id} style={{ paddingTop: first ? 0 : 10 }}>
      <h3 style={{ margin: 0 }}>
        <Bar data-md-info-toggle={id} icon={icon} label={title} count={count}
          open={open} onToggle={() => setOpen(!open)} />
      </h3>
      {open && <div style={{ padding: '6px 2px 2px' }}>{children}</div>}
    </section>;
  }

  /* ── THE END OF A RECORD, AT THE END OF WHAT IT IS (P-6) ──────────────────
     Delete was the one real function in the foot toolbar's More menu, and the
     toolbar retired with the rest of the compose sheet's old body. This is
     where it lands: after every section, below the facts it is about to remove,
     ruled off from them — because it is not a fact about the record, it is the
     end of one.

     Quiet by default and red only on the reach. A destructive control that
     shouts from a panel you opened to read a backlink is a panel you learn to
     open carefully; one that answers when you go to it is a control you can
     find without being warned at every glance. It does not delete — it asks,
     through the sheet's own ConfirmDialog, which is where the record's name
     already is. */
  function DeleteRow({ label, onPress }) {
    const light = isLightPR();
    const red = light ? '#E11D48' : '#F43F5E';
    return <div style={{ marginTop: 18, paddingTop: 14,
      borderTop: `0.75px solid ${inkPR(light ? 0.08 : 0.12)}` }}>
      <button type="button" aria-label={label} onClick={onPress}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%',
          minHeight: 34, padding: '6px 10px', border: 'none',
          borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
          color: inkPR(0.6), font: 'inherit', fontSize: 13, cursor: 'pointer',
          textAlign: 'start', transition: 'background .14s, color .14s' }}
        onPointerEnter={(e) => {e.currentTarget.style.background = window.mdRgba(red, 0.1);
          e.currentTarget.style.color = red;}}
        onPointerLeave={(e) => {e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = inkPR(0.6);}}
        onFocus={(e) => {e.currentTarget.style.color = red;}}
        onBlur={(e) => {e.currentTarget.style.color = inkPR(0.6);}}>
        <span aria-hidden="true" style={{ display: 'inline-flex', lineHeight: 0 }}>
          {window.MDI && window.MDI.trash ? window.MDI.trash(red, 16) : null}</span>
        <span>{label}</span>
      </button>
    </div>;
  }

  function MdInfoPanel({ record, accent, scheduled = null, projects = null,
    onPickProject = null, onPickDomain = null, onRecordStatus = null,
    pendingStatus = null, onPendingStatus = null,
    dayLabel = '', schedText = '', whenLabel = 'Schedule', onWhen = null,
    linkGroups = null, onOpenLinked = null,
    tagState = null, onTagAdd = null, onTagRemove = null,
    onDelete = null, deleteLabel = 'Delete record',
    placement = 'overlay' }) {
    const writeStatus = (id) => {
      if (onPendingStatus && pendingStatus !== null) { onPendingStatus(id); return; }
      if (onRecordStatus) { onRecordStatus({ uid: record.uid, record, scheduled }, id); return; }
      if (window.mdRecordTrySetStatus) window.mdRecordTrySetStatus(record, id, scheduled);
    };

    const ctx = { record, accent, scheduled, dayLabel, schedText, whenLabel, onWhen,
      onPickProject, onPickDomain, pendingStatus, writeStatus, onOpenLinked,
      onTagAdd, onTagRemove,
      projects: projects || (window.mdProjectIndex ? window.mdProjectIndex({ open: true }) : []),
      links: { outgoing: [], backlinks: [], unresolved: [], ...(linkGroups || {}) },
      tags: { active: [], options: [], sourceRef: null, ...(tagState || {}) } };

    return <MdInfoShell placement={placement}>
      {INFO_SECTIONS.map((sec, at) =>
        <MdInfoSection key={sec.id} id={sec.id} icon={sec.icon} title={sec.title} first={at === 0}
          count={sec.count ? sec.count(ctx) : 0}>
          {sec.body(ctx)}
        </MdInfoSection>)}
      {onDelete ? <DeleteRow label={deleteLabel} onPress={onDelete} /> : null}
    </MdInfoShell>;
  }

  Object.assign(window, { MdInfoPanel, INFO_SECTIONS,
    MdInfoShell, MdInfoSection, MdInfoRow: InfoRow });
})();
